/*#
# crud

<!--{ "parent": "Components" }-->

Search, list, edit — [`<tosi-table>`](/data-table/) and
[`<tosi-schema-form>`](/schema-form/) over a store you supply.

```js
import { tosiCrud } from 'tosijs-ui'

// A store is three promise-returning methods. This one is an array in a closure; yours
// might be `fetch`, a DocStore, or IndexedDB — the component neither knows nor cares.
let records = [
  { id: 1, name: 'Ada Lovelace', role: 'admin', active: true },
  { id: 2, name: 'Grace Hopper', role: 'admin', active: true },
  { id: 3, name: 'Katherine Johnson', role: 'editor', active: false },
]

preview.append(
  tosiCrud({
    hashNamespace: 'people',
    schema: {
      type: 'object',
      properties: {
        id: { type: 'integer', title: 'ID' },
        name: { type: 'string' },
        role: { type: 'string', enum: ['admin', 'editor', 'viewer'] },
        active: { type: 'boolean' },
      },
      required: ['name'],
    },
    store: {
      async list({ search }) {
        const term = (search ?? '').toLowerCase()
        return records.filter((r) => r.name.toLowerCase().includes(term))
      },
      async save(record) {
        const id = record.id ?? Math.max(0, ...records.map((r) => r.id)) + 1
        const saved = { ...record, id }
        records = records.some((r) => r.id === id)
          ? records.map((r) => (r.id === id ? saved : r))
          : [...records, saved]
        return saved
      },
      async delete(record) {
        records = records.filter((r) => r.id !== record.id)
      },
    },
  })
)
```
```test
const crud = await waitFor('tosi-crud')
await crud.whenIdle()

test('search, select, edit, save — and the URL remembers where you were', async () => {
  // One test: every step shares this component, and test() bodies run concurrently.
  expect(crud.rows.length).toBe(3)

  // Selecting a row loads it into the form and records it in the hash.
  crud.select(crud.rows[1])
  await crud.whenIdle()
  expect(crud.value.name).toBe('Grace Hopper')
  expect(location.hash).toContain('people.id=2')

  // Editing writes the form's model; saving sends it to the store and re-lists.
  const name = crud.form.querySelector('[data-path="name"]')
  name.value = 'Grace B. Hopper'
  name.dispatchEvent(new Event('input', { bubbles: true }))
  await crud.save()
  expect(crud.rows.find((r) => r.id === 2).name).toBe('Grace B. Hopper')

  // Searching re-queries the store.
  crud.search = 'ada'
  await crud.whenIdle()
  expect(crud.rows.length).toBe(1)
})
```

## The store is the only thing you have to write

```typescript
interface CrudStore {
  list(query: { search?: string }): Promise<any[]>
  save?(record: any): Promise<any>     // returns the saved record
  delete?(record: any): Promise<void>
}
```

There is **no transport in here** — no `fetch`, no URL convention, no envelope format. REST,
a DocStore, IndexedDB, an array in a closure and a mock in a test all satisfy the same three
methods. Omit `save` and the form is read-only; omit `delete` and its button is not shown.

`save` returns the saved record because the server usually knows things the client does not:
the id of a new record, a timestamp, a computed field. That returned record is what the form
then holds.

## The parts stay usable on their own

`<tosi-crud>` composes public components and exposes them — `.table` and `.form` are the real
elements, so anything you can do to a `<tosi-table>` you can do here:

```js
import { tosiCrud } from 'tosijs-ui'

const crud = tosiCrud({
  store: { async list() { return [{ id: 1, name: 'Ada', role: 'admin' }] } },
})
preview.append(crud)

// `.table` and `.form` are the real elements — once the component has hydrated.
await new Promise((r) => requestAnimationFrame(r))
crud.table.columns = [
  { prop: 'name', width: 200 },
  { prop: 'role', width: 100 },
]
crud.form.readOnly = true
```
```test
const crud = await waitFor('tosi-crud')
await crud.whenIdle()

test('the composed parts are reachable, and the wrapper does not undo you', async () => {
  expect(crud.table.columns.map((c) => c.prop)).toEqual(['name', 'role'])
  // `readOnly` used to be reassigned on every queued render, so this reverted a frame later
  // — the second line of a two-line example, silently undone.
  crud.form.readOnly = true
  await new Promise((r) => requestAnimationFrame(r))
  await new Promise((r) => requestAnimationFrame(r))
  expect(crud.form.readOnly).toBe(true)
})
```

Before it hydrates, `.table` and `.form` are `null` rather than throwing — so a guard reads
as a guard:

It is a convenience, never the only way to reach them. Compose the three yourself when your
layout wants something else — that is a supported thing to do, not a fallback.

## Schema: give one, or let it infer

With a `schema`, the form renders it and the table takes its columns **from the schema** —
which is better than the table's own inference, because that reads `Object.keys(array[0])` and
so loses the column for any property the first row happens not to have.

Without one, the form infers a schema from the record it edits (see
[schema-form](/schema-form/)) and the table falls back to inferring its own columns. That
works **provided a validator is registered** — inference is its `inferSchema`, not a lazy
import (the seam replaced that). With no validator and no schema the table still lists rows
from its own inference while the form has nothing to render, which is the most confusing
combination there is, so give a schema when you have one.

```js
import { tosiCrud } from 'tosijs-ui'

let rows = [
  { sku: 'W-1', qty: 2, price: 9.99 },
  { sku: 'G-9', qty: 5, price: 1.5 },
]

preview.append(
  tosiCrud({
    hashNamespace: 'stock',
    idPath: 'sku',
    schema: {
      type: 'object',
      properties: {
        sku: { type: 'string', title: 'SKU' },
        qty: { type: 'integer', title: 'Quantity' },
        price: { type: 'number' },
      },
      required: ['sku'],
    },
    store: {
      async list() {
        return rows
      },
      async save(record) {
        rows = rows.some((r) => r.sku === record.sku)
          ? rows.map((r) => (r.sku === record.sku ? record : r))
          : [...rows, record]
        return record
      },
    },
  })
)
```
```test
const stock = await waitFor('tosi-crud')
await stock.whenIdle()

test('columns come from the schema, titles and all', () => {
  expect(stock.table.columns.map((c) => c.prop)).toEqual(['sku', 'qty', 'price'])
  expect(stock.table.columns[0].name).toBe('SKU')
  // No `delete` on the store, so no delete button is shown — a disabled or dead button
  // advertises an affordance that does not exist.
  expect(stock.querySelector('.crud-delete').hidden).toBe(true)
  expect(stock.querySelector('.crud-save').hidden).toBe(false)
})
```

## The URL remembers where you were

The search term and the selected record's id live in the page hash via
[`hashState`](/hash-state/), so a filtered list with a record open is a link you can send
someone. Set `hashNamespace` to keep two of them from colliding, or `hashMode="memory"` to
keep the URL out of it entirely.

Typing **replaces** the current history entry; selecting a record **pushes** one — so back
takes you out of the record you opened rather than un-typing your search one letter at a time.

## Failures are shown, never swallowed

If `list`, `save` or `delete` rejects, the message is displayed and an `error` event is
dispatched with `{ operation, error }`. Nothing is retried behind your back and nothing
reports success on a failure.

Out-of-order responses are dropped: every `list` carries a sequence number and a late reply
from a slower earlier query is discarded rather than overwriting newer results. That is the
classic search-as-you-type bug — you stop typing and the list flips back to the results for a
prefix.

```js
import { tosiCrud } from 'tosijs-ui'

const wait = (ms) => new Promise((r) => setTimeout(r, ms))
const all = ['alpha', 'alps', 'alphabet'].map((name, id) => ({ id, name }))

preview.append(
  tosiCrud({
    hashNamespace: 'slow',
    store: {
      async list({ search }) {
        // A short term is SLOWER, so a stale reply is guaranteed to land last — which is
        // exactly the race a real network produces by accident.
        await wait(search && search.length > 2 ? 0 : 150)
        return all.filter((r) => r.name.startsWith(search ?? ''))
      },
      async save() {
        throw new Error('the server said no')
      },
    },
  })
)
```
```test
const slow = await waitFor('tosi-crud')
await slow.whenIdle()

test('a stale reply is dropped, and a failed save is reported not swallowed', async () => {
  // Fire the slow query, then the fast one, without waiting in between.
  slow.search = 'al'
  slow.search = 'alph'
  await slow.whenIdle()
  // 'alph' matches alpha + alphabet; 'al' would have matched all three. The late reply from
  // the earlier query must not put the list back to a prefix the user has finished typing.
  expect(slow.rows.map((r) => r.name)).toEqual(['alpha', 'alphabet'])

  // A rejecting save reports the failure three ways and claims success none of them.
  slow.select(slow.rows[0])
  let event = null
  slow.addEventListener('error', (e) => (event = e.detail), { once: true })
  let threw = false
  try {
    await slow.save()
  } catch (e) {
    threw = true
  }
  await slow.whenIdle()
  expect(threw).toBe(true)
  expect(event.operation).toBe('save')
  expect(slow.querySelector('.crud-status').textContent).toBe('the server said no')
})
```

## Closing a record, and narrow layouts

An open record shows Save and Delete (when its store can save and delete) and **Close** under
its form. Close (or `close()` from code)
puts the record away and goes back to the list: it clears the selection (and the `id` in the
URL), and it drops any unsaved edits without asking, so call `save()` first if they matter.

When the element is narrower than `TosiCrud.NARROW_WIDTH` (720px) it gets the class `-narrow`
and stacks into one column, and an open record **replaces** the list rather than squeezing in
beside it; Close is the way back. The width is the element's own, measured with a
`ResizeObserver`, not the window's, so a crud in a sidebar goes narrow on a wide screen.
`NARROW_WIDTH` is a static property, so setting `TosiCrud.NARROW_WIDTH` moves the breakpoint
for every instance; each one re-checks it the next time it is resized.

## Properties, methods, events

- `store` — the adapter. Setting it re-lists.
- `schema` — optional JSON Schema for the form and the table's columns.
- `idPath` — which property identifies a record (default `'id'`).
- `search` — the current search term.
- `rows` — the records currently listed.
- `value` — the selected record (the form's live model).
- `table` / `form` — the composed elements.
- `refresh()` / `select(record)` / `save()` / `remove()` / `createNew()`
- `close()` — put the open record away (unsaved edits are dropped); what the Close button does.
- `TosiCrud.NARROW_WIDTH` — below this width (720) the layout is one column; see above.
- `whenIdle()` — resolves when no store operation is in flight; for tests, mostly.
- `change` — the selection or the saved record changed.
- `error` — a store operation failed.
*/

/*{ "headTitle": "<tosi-crud> list-and-edit screens — tosijs-ui", "parent": "Components", "description": "<tosi-crud> pairs a searchable table with a schema-driven form over a store you supply (REST, IndexedDB or an array) for list-and-edit screens." }*/

import { ElementCreator, elements, withAttributes } from 'tosijs'
import type { JSONSchema } from './schema-form/json-schema.js'
import { tosiTable, type ColumnOptions, type TosiTable } from './data-table.js'
import { tosiSchemaForm, type TosiSchemaForm } from './schema-form.js'
import { hashState, type HashState, type HashStateMode } from './hash-state.js'
import { localize } from './localize.js'
import { getByPath, fieldForProperty } from './schema-form/fields.js'

const { div, input, button } = elements

export interface CrudQuery {
  search?: string
  [key: string]: unknown
}

/**
 * Everything the component needs from your data layer.
 *
 * Three promise-returning methods and no transport: the point is that REST, a DocStore,
 * IndexedDB, an in-memory array and a mock all satisfy this without the component learning
 * anything about any of them.
 */
export interface CrudStore {
  list(query: CrudQuery): Promise<any[]>
  /** returns the saved record — the server usually knows the id, the timestamp, the rest */
  save?(record: any): Promise<any>
  delete?(record: any): Promise<void>
}

interface CrudParts {
  search: HTMLInputElement
  table: TosiTable
  form: TosiSchemaForm
  detail: HTMLElement
  status: HTMLElement
  saveButton: HTMLButtonElement
  deleteButton: HTMLButtonElement
  newButton: HTMLButtonElement
  closeButton: HTMLButtonElement
}

/*
Columns from a schema, so ONE description of the shape drives both surfaces.

`<tosi-table>`'s own inference reads `Object.keys(array[0])`, so a property missing from the
first row silently loses its column. When a schema is given it is the better source: it knows
every property, their titles, and their types — including ones no row happens to have filled
in.
*/
export function columnsFromSchema(schema: JSONSchema): ColumnOptions[] {
  if (!schema?.properties) return []
  return Object.keys(schema.properties).map((prop) => {
    /*
    Ask the MODEL what this property is, rather than re-reading `type` here.

    This used to unwrap `type` itself — a third copy of the nullable idiom — and got the
    common `{anyOf: [{type:'boolean'}, {type:'null'}]}` spelling wrong: no `type` on the
    column, so a wide text column rendering raw `true`/`false`. `enum` and `const` missed the
    same way. `fieldForProperty` already answers this, and it is the same answer the form
    uses, which is the entire point of the model being DOM-free.
    */
    const field = fieldForProperty(schema, prop)
    const column: ColumnOptions = {
      prop,
      // `field.label` is the schema's `title` when it has one and a humanised property name
      // when it does not. Emitting only `title` left the table rendering `firstName` while
      // the form showed "first name" — worse than the no-schema path, which humanises.
      name: field?.label,
      width:
        field?.kind === 'boolean'
          ? 80
          : field?.kind === 'integer' || field?.kind === 'number'
          ? 100
          : 180,
    }
    if (field?.kind === 'boolean') column.type = 'boolean(check,x)' as any
    return column
  })
}

export class TosiCrud extends withAttributes({
  idPath: 'id',
  hashNamespace: 'crud',
  hashMode: 'hash',
  /** ms to wait after a keystroke before querying — a remote store is not free */
  searchDelay: 200,
})<CrudParts> {
  static preferredTagName = 'tosi-crud'

  static lightStyleSpec = {
    ':host': {
      display: 'grid',
      gridTemplateRows: 'auto minmax(0, 1fr) auto',
      gap: 'var(--tosi-spacing, 10px)',
      minHeight: '300px',
      // Fill a container that has a height (a live example's preview, a panel), so the list
      // and the record scroll inside it instead of spilling out. In a container with no height
      // this is `auto`, as before.
      height: '100%',
      boxSizing: 'border-box',
    },
    // A page's own `button { display: … }` must not resurrect a button this component hid:
    // the list-only store's Save and Delete showed on the doc site.
    ':host button[hidden]': { display: 'none' },
    ':host .crud-toolbar': {
      display: 'flex',
      gap: 'var(--tosi-spacing-sm, 8px)',
      alignItems: 'center',
    },
    ':host .crud-search': { flex: '1 1 auto', minWidth: '0' },
    ':host .crud-body': {
      display: 'grid',
      gridTemplateColumns: 'minmax(0, 2fr) minmax(0, 1fr)',
      gap: 'var(--tosi-spacing, 10px)',
      minHeight: '0',
    },
    ':host .crud-detail': {
      display: 'grid',
      gridTemplateRows: 'minmax(0, 1fr) auto',
      gap: 'var(--tosi-spacing-sm, 8px)',
      overflow: 'auto',
    },
    ':host .crud-detail[hidden]': { display: 'none' },
    /*
    Narrow (under NARROW_WIDTH): one column, and an open record REPLACES the list rather than
    squeezing in beside it, with Close to go back. Side by side at 650px left the form a
    210px column in the doc preview.
    */
    ':host.-narrow .crud-body': { gridTemplateColumns: 'minmax(0, 1fr)' },
    ':host.-narrow.-detail-open tosi-table': { display: 'none' },
    ':host .crud-actions': {
      display: 'flex',
      gap: 'var(--tosi-spacing-sm, 8px)',
    },
    ':host .crud-status': { fontSize: '0.85em', opacity: '0.8' },
    ':host .crud-status.-error': {
      color: 'var(--tosi-error, #c00)',
      opacity: '1',
    },
    ':host tosi-table': { minHeight: '200px' },
    ':host .crud-actions .elastic': { flex: '1 1 auto' },
  }

  private _store: CrudStore | null = null
  private _schema: JSONSchema | null = null
  private _rows: any[] = []
  private _selected: any = null
  /** True between `createNew()` and the next save/select — see `syncSelectionFromHash`. */
  private _creating = false
  /*
  The exact object last pushed INTO the form — the ONE thing that decides whether to load.

  It has to be tracked separately because editing produces a NEW object every keystroke:
  `setByPath` is immutable, so `form.value !== _selected` is permanently true after the first
  character. The previous guard compared exactly that and therefore wrote the record as
  loaded back over the user's edit on the next render — one frame later, with no other
  action, and `save()` then posted the unedited record.

  `_selected` deliberately stays at the record as LOADED. Nothing needs it to track the edit:
  `value` and `save()` read `form.value` directly, and `remove()` wants the identity of the
  record that was opened rather than a half-edited copy of it.
  */
  private _loaded: any = null
  private _error = ''
  private _pending = 0
  /*
  Every `list` carries a sequence number and only the newest reply is accepted.

  Without it, a slow query for `a` can land after a fast one for `abc` and put the list back
  to the results for a prefix the user has already finished typing — the classic
  search-as-you-type bug, and one that only shows up on a real network.
  */
  private _listSeq = 0
  private _idle: Array<() => void> = []
  private _hash: HashState | null = null
  private _stopHash: (() => void) | null = null
  private _searchTimer: ReturnType<typeof setTimeout> | undefined
  /** What the box says, before the debounce commits it to the URL. */
  private _pendingSearch = ''

  get store(): CrudStore | null {
    return this._store
  }

  set store(store: CrudStore | null) {
    this._store = store
    if (this.hydrated) void this.refresh()
  }

  get schema(): JSONSchema | null {
    return this._schema
  }

  set schema(schema: JSONSchema | null) {
    this._schema = schema
    this.queueRender()
  }

  get rows(): any[] {
    return this._rows
  }

  /** The selected record — the form's live model, so it includes unsaved edits. */
  get value(): any {
    return this.hydrated && this._selected
      ? this.parts.form.value
      : this._selected
  }

  /*
  Guarded, like every other accessor on this class.

  `this.parts.<name>` THROWS before hydration, and these two were the only accessors without
  the `hydrated` check that `value`, `search`, `store` and `render` all have — so the
  documented `tosiCrud({store}).table` threw `elementRef "table" does not exist!` on the line
  the docs told you to write. `null` is the honest answer for "not built yet"; it is also what
  makes the guard visible at the call site instead of at a stack trace.
  */
  get table(): TosiTable | null {
    return this.hydrated ? this.parts.table : null
  }

  get form(): TosiSchemaForm | null {
    return this.hydrated ? this.parts.form : null
  }

  get search(): string {
    // What the box says wins over what the URL has caught up to — otherwise reading `search`
    // mid-debounce reports a stale term, which is what `refresh()` would then query for.
    return this._pendingSearch || (this._hash?.get('q') ?? '')
  }

  set search(term: string) {
    this._pendingSearch = term
    this._hash?.set('q', term || undefined)
    if (this.hydrated) this.parts.search.value = term
    void this.refresh()
  }

  /**
   * Resolves when no store operation is in flight AND the DOM has caught up.
   *
   * Both halves matter to a caller: rendering is queued for the next frame, so "the store
   * answered" and "the list shows the answer" are different moments.
   */
  async whenIdle(): Promise<void> {
    if (this._pending > 0) {
      await new Promise<void>((resolve) => this._idle.push(resolve))
    }
    await new Promise((resolve) => requestAnimationFrame(resolve))
  }

  private settle(): void {
    if (this._pending > 0) return
    const waiting = this._idle
    this._idle = []
    for (const resolve of waiting) resolve()
  }

  /*
  One place where a store call is awaited, so nothing can quietly succeed on a failure.

  A rejection is shown, dispatched as an `error` event, and re-thrown to the caller of the
  public method — a `save()` that failed must not resolve as if it worked.
  */
  private async run<T>(operation: string, work: () => Promise<T>): Promise<T> {
    this._pending++
    this._error = ''
    this.queueRender()
    try {
      return await work()
    } catch (error) {
      this._error = (error as Error)?.message ?? String(error)
      this.dispatchEvent(
        new CustomEvent('error', { detail: { operation, error } })
      )
      throw error
    } finally {
      this._pending--
      this.queueRender()
      this.settle()
    }
  }

  async refresh(): Promise<void> {
    if (!this._store) return
    const seq = ++this._listSeq
    const rows = await this.run('list', () =>
      this._store!.list({ search: this.search || undefined })
    )
    // A late reply from an earlier query is not news — drop it rather than overwrite the
    // results of the query the user is actually waiting on.
    if (seq !== this._listSeq) return
    this._rows = rows ?? []
    // A fresh list invalidates what we told the table about the old one.
    this._tableSelection = null
    this.queueRender()
  }

  select(record: any): void {
    this._creating = false
    this._selected = record ?? null
    // Into the form NOW, not at the next render: `crud.value` is the form's model, and a
    // caller that selects and then reads it back should not have to wait a frame.
    this.showSelected()
    const id = record ? getByPath(record, this.idPath) : undefined
    // A selection is somewhere to come BACK from, so it pushes; typing replaces.
    this._hash?.set(
      'id',
      id === undefined || id === null ? undefined : String(id),
      { push: true }
    )
    this.queueRender()
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /** Close the open record and go back to the list. Unsaved edits are dropped. */
  close(): void {
    this.select(null)
  }

  /** Narrow enough that the list and the record cannot sit side by side. */
  static NARROW_WIDTH = 720
  private _resize: ResizeObserver | null = null
  private measure = (): void => {
    const narrow =
      this.clientWidth > 0 && this.clientWidth < TosiCrud.NARROW_WIDTH
    this.classList.toggle('-narrow', narrow)
  }

  /*
  A blank record to start from. With a schema the form knows the fields; without one it infers
  them from the record it edits, and from `{}` that is nothing at all, so New showed "This schema
  describes no properties". So without a schema, start from the fields the listed rows have,
  each empty of its kind, leaving out the id (the store assigns it).
  */
  private blankRecord(): any {
    if (this._schema) return {}
    const blank: Record<string, unknown> = {}
    for (const row of this._rows) {
      for (const [key, value] of Object.entries(row ?? {})) {
        if (key === this.idPath || key in blank) continue
        blank[key] =
          typeof value === 'number'
            ? 0
            : typeof value === 'boolean'
            ? false
            : typeof value === 'string'
            ? ''
            : null
      }
    }
    return blank
  }

  /** Start a blank record. Nothing is stored until `save()`. */
  createNew(): void {
    this._creating = true
    this._selected = this.blankRecord()
    this.showSelected()
    this._hash?.set('id', undefined)
    this.queueRender()
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  async save(): Promise<any> {
    if (!this._store?.save || !this._selected) return
    const record = this.parts.form.value
    const saved = await this.run('save', () => this._store!.save!(record))
    // The store's answer wins: it knows the new id, the timestamp, the computed fields.
    this._creating = false
    /*
    A store that returns nothing keeps the record it was given.

    `save` is typed `Promise<any>`, and `Promise<void>` satisfies that — a 204-style adapter
    returning nothing is a perfectly ordinary implementation. `saved ?? record` already handled
    it; what did not was everything downstream assuming the result carried an id.
    */
    this._selected = saved ?? record
    /*
    A saved record HAS an id now, so the URL should point at it.

    Without this the hash stays id-less after saving a new record, and once absence of `id`
    means "deselect" (which is what makes Back work), the very next render threw the
    just-saved record away. Writing it also makes the obvious thing true: save a new record
    and the address bar is a link to it.
    */
    const savedId = getByPath(this._selected ?? {}, this.idPath)
    if (savedId !== undefined && savedId !== null) {
      this._hash?.set('id', String(savedId))
    }
    this.showSelected()
    await this.refresh()
    this.queueRender()
    this.dispatchEvent(new Event('change', { bubbles: true }))
    return this._selected
  }

  /**
   * Is the selected record something the store could delete?
   *
   * ONE rule, used by both the button and the method. They disagreed: `render()` disabled the
   * button for a record with no id, while `remove()` only checked that *something* was
   * selected — so `createNew()` followed by `remove()` sent `store.delete({})`, which for a
   * REST adapter is `DELETE /records/undefined`. A guard the UI enforces and the API does not
   * is not a guard.
   */
  private get deletable(): boolean {
    return (
      Boolean(this._store?.delete) &&
      Boolean(this._selected) &&
      getByPath(this._selected ?? {}, this.idPath) !== undefined
    )
  }

  async remove(): Promise<void> {
    if (!this.deletable) return
    await this.run('delete', () => this._store!.delete!(this._selected))
    this._selected = null
    this._loaded = null
    this._hash?.set('id', undefined)
    await this.refresh()
    this.queueRender()
    this.dispatchEvent(new Event('change', { bubbles: true }))
  }

  /*
  The URL write is INSIDE the debounce, with the query.

  It used to be the first statement, so every keystroke called `history.replaceState`. WebKit
  throws `SecurityError` past ~100 calls in 10s — ordinary typing does not reach that, but a
  held key clearing a long term does in a few seconds, and the counter is shared with crud's
  own `select()` pushes and any hash router on the page. When it threw, the throw preceded the
  `setTimeout`, so NO query was scheduled either: search stopped working, with an uncaught
  error, and `get search()` reads from the hash so crud's own idea of the term went stale too.

  The debounce already exists because a remote store should not be queried per keystroke. A
  URL that only a human reads has exactly the same argument.
  */
  private handleSearchInput = (event: Event): void => {
    const term = (event.target as HTMLInputElement).value
    this._pendingSearch = term
    clearTimeout(this._searchTimer)
    this._searchTimer = setTimeout(() => {
      this._hash?.set('q', this._pendingSearch || undefined)
      void this.refresh()
    }, this.searchDelay)
  }

  private handleSelectionChanged = (selected: any[]): void => {
    /*
    Ignore the table's notification while WE are the ones driving it (tosijs-ui#157).

    `selectRow`/`deSelect` became notifying, so `syncTableSelection` — which deselects and
    then re-selects — now calls back into here twice. The deselect half reports an empty
    selection, so this cleared `_selected`, and the record vanished mid-edit.

    `_applyingSelection` already existed to stop `syncTableSelection` re-entering itself; the
    notification is a second path out of the same operation and needs the same guard. A
    component that both drives and observes a selection has to be able to tell its own writes
    from the user's, and this is that line.
    */
    if (this._applyingSelection) return
    this.select(selected[0] ?? null)
  }

  content = () => [
    div(
      { class: 'crud-toolbar' },
      input({
        part: 'search',
        class: 'crud-search',
        type: 'search',
        placeholder: localize('search…'),
        onInput: this.handleSearchInput,
      }),
      button({ part: 'newButton', class: 'crud-new' }, localize('New'))
    ),
    div(
      { class: 'crud-body' },
      tosiTable({ part: 'table', select: true }),
      div(
        { part: 'detail', class: 'crud-detail' },
        tosiSchemaForm({ part: 'form' }),
        div(
          { class: 'crud-actions' },
          button({ part: 'saveButton', class: 'crud-save' }, localize('Save')),
          button(
            { part: 'deleteButton', class: 'crud-delete' },
            localize('Delete')
          ),
          div({ class: 'elastic' }),
          button(
            { part: 'closeButton', class: 'crud-close' },
            localize('Close')
          )
        )
      )
    ),
    div({ part: 'status', class: 'crud-status' }),
  ]

  connectedCallback(): void {
    super.connectedCallback()
    this._hash = hashState({
      namespace: this.hashNamespace,
      mode: this.hashMode as HashStateMode,
    })
    // The back button changes the hash without going through us.
    this._stopHash = this._hash.observe(() => this.queueRender())

    this.parts.table.selectionChanged = this.handleSelectionChanged
    this.parts.newButton.onclick = () => this.createNew()
    this.parts.closeButton.onclick = () => this.close()
    if (typeof ResizeObserver === 'function') {
      this._resize = new ResizeObserver(this.measure)
      this._resize.observe(this)
    }
    /*
    Swallow the rejection AT THE BUTTON, and only here.

    `run()` has already shown the message and dispatched `error`, so the user has been told;
    what `void promise` added was an unhandled rejection in their console on top of it — and
    in a page with a global handler, a spurious crash report. The methods still reject for a
    programmatic caller, which is where a caller can actually do something about it.
    */
    this.parts.saveButton.onclick = () => {
      this.save().catch(() => undefined)
    }
    this.parts.deleteButton.onclick = () => {
      this.remove().catch(() => undefined)
    }
    this.parts.form.addEventListener('change', () => this.queueRender())
    this.parts.search.value = this.search

    void this.refresh()
  }

  disconnectedCallback(): void {
    this._resize?.disconnect()
    this._resize = null
    clearTimeout(this._searchTimer)
    this._stopHash?.()
    this._hash?.stop()
    super.disconnectedCallback()
  }

  /*
  Reconnect the selection from the hash.

  This is what makes a link to a record work, and what makes the back button walk back out of
  one. It runs whenever the rows change too, so a record that was not in the list yet gets
  picked up once it is.
  */
  /*
  Keep the TABLE's selection in step with ours.

  `select()` loaded the form and wrote the hash and never told the table, so clicking a row
  then selecting another programmatically left the first row highlighted while the form
  showed the second — and a `#?people.id=2` deep link, the feature this release headlines,
  opened the record with nothing highlighted at all. "A link you can send someone" produced a
  list that did not show where you were.

  Guarded by `_applyingSelection` because the table's own `selectionChanged` calls back into
  `select()`; without it a click would recurse.
  */
  private _applyingSelection = false
  /*
  What we last TOLD the table, which is not the same question as what the table reports.

  Reading `table.selectedRows` to decide whether to act looks equivalent and is not: before
  the rows have stamped it is empty, so every render re-applied the selection, and
  `selectRow`/`deSelect` mutate row objects and queue a table render — which rebuilt the
  cells on every keystroke. Chromium stamped fast enough to hide it; WebKit did not, which is
  the whole argument for running three engines.

  Same shape as `_loaded` for the form: remember what you sent, not what you can observe.
  */
  private _tableSelection: any = null

  private syncTableSelection(): void {
    if (!this.hydrated || this._applyingSelection) return
    if (this._tableSelection === this._selected) return
    const table = this.parts.table
    this._applyingSelection = true
    try {
      table.deSelect()
      if (this._selected && this._rows.includes(this._selected)) {
        table.selectRow(this._selected)
      }
      this._tableSelection = this._selected
    } finally {
      this._applyingSelection = false
    }
  }

  /** Put the selected record into the form. Idempotent, so render can call it too. */
  private showSelected(): void {
    if (!this.hydrated) return
    this.parts.detail.hidden = !this._selected
    this.classList.toggle('-detail-open', Boolean(this._selected))
    // Load only when the SELECTION changed. A render must never push a record the user is
    // in the middle of editing.
    if (this._selected && this._loaded !== this._selected) {
      this.parts.form.value = this._selected
      this._loaded = this._selected
    }
    this.syncTableSelection()
  }

  private syncSelectionFromHash(): void {
    const id = this._hash?.get('id')
    /*
    An ABSENT id means deselect — unless we are mid-`createNew()`.

    This used to return early whenever `id` was missing, which made it structurally unable to
    ever deselect: after `history.back()` the hash was clean and the detail pane still showed
    the record you had opened, while RELOADING that same URL showed nothing. Back-then-reload
    changed what you saw, and the docs promise the opposite.

    `_creating` is what lets absence be unambiguous: a blank new record has no id and must
    survive, a popped history entry has no id and must not.
    */
    if (id === undefined) {
      if (this._creating || !this._selected) return
      this._selected = null
      this._loaded = null
      this.dispatchEvent(new Event('change', { bubbles: true }))
      return
    }
    const current =
      this._selected && String(getByPath(this._selected, this.idPath))
    if (current === id) return
    const match = this._rows.find(
      (row) => String(getByPath(row, this.idPath)) === id
    )
    if (match) {
      this._selected = match
      // Documented as "the selection or the saved record changed" — a hash-driven selection
      // is a selection change, and it fired nothing.
      this.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  render(): void {
    super.render()
    this.syncSelectionFromHash()

    if (this._schema) {
      if (this.parts.form.schema !== this._schema) {
        this.parts.form.schema = this._schema
      }
      // Set columns once: replacing them on every render would throw away a consumer's own
      // column widths and hidden/pinned state along with them.
      if (!this.parts.table.columns?.length) {
        this.parts.table.columns = columnsFromSchema(this._schema)
      }
    }
    /*
    Identity guards. `TosiTable.set array` always queues a render and `TosiTable.render()`
    starts with `this.textContent = ''`, so an unconditional assignment tore the whole table
    down — and crud queues a render on every form `change`, every hashState notification and
    every pending transition. Measured: 8 keystrokes → 8 full table rebuilds. The search
    debounce protects the network; nothing protected the DOM.
    */
    if (this.parts.table.array !== this._rows) {
      this.parts.table.array = this._rows
    }

    this.showSelected()

    const canSave = Boolean(this._store?.save)
    const canDelete = Boolean(this._store?.delete)
    this.parts.saveButton.hidden = !canSave
    // No `delete` on the store means no button — a disabled one advertises an affordance
    // that does not exist.
    this.parts.deleteButton.hidden = !canDelete
    this.parts.newButton.hidden = !canSave
    /*
    Only FORCE read-only when the store cannot save. Otherwise leave it alone.

    This was unconditional, so a consumer setting `crud.form.readOnly = true` — which the docs
    showed as the way to reach the composed parts — had it silently reverted on the very next
    queued render. "The parts stay usable on their own" has to mean the wrapper does not
    reach in and undo you.
    */
    if (!canSave) this.parts.form.readOnly = true
    this.parts.saveButton.disabled = this._pending > 0
    this.parts.deleteButton.disabled = this._pending > 0 || !this.deletable

    this.parts.status.classList.toggle('-error', Boolean(this._error))
    this.parts.status.textContent = this._error
      ? this._error
      : this._pending > 0
      ? localize('loading…')
      : localize('{count} records', { count: this._rows.length })
  }
}

export const tosiCrud = TosiCrud.elementCreator() as ElementCreator<TosiCrud>
